import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  FormGroup,
  ControlLabel,
  HelpBlock,
  FormControl,
  InputGroup,
  Alert,
  DropdownButton,
  MenuItem
} from "react-bootstrap";
import { find, prop, equals, head } from "ramda";
import questionTypes from "../questionTypes.json";
import unitOfMeasure, { getUnitsOfMeasure } from '../common/unitOfMeasure';

const getAnswerId = prop("answerId");
const findAnswerForOption = option =>
  find(answer => equals(getAnswerId(option), getAnswerId(answer)));

const ftUnitOfMeasure = 'ft';

class HeightQuestion extends Component {
  constructor(props) {
    super(props)

    const { readOnly, options, /* answers */} = props
    const option = head(options);

    let displayValue = ''
    let selectedUnitOfMeasure = unitOfMeasure(option)

    if (readOnly || (option.meta && option.meta.unitOfMeasure)) {
      displayValue = option.meta.displayValue
      selectedUnitOfMeasure = option.meta.unitOfMeasure
    }

    this.state = {
      selectedUnitOfMeasure,
      displayValue
    }

    this.renderWithUnitsOfMeasure = this.renderWithUnitsOfMeasure.bind(this)
    this.renderWithoutUnitsOfMeasure = this.renderWithoutUnitsOfMeasure.bind(this)
    this.unitOfMeasureSelected = this.unitOfMeasureSelected.bind(this)
  }

  unitOfMeasureSelected(unitOfMeasure) {
    const {
      questionId,
      onAnswer,
      options,
      answers
    } = this.props

    const option = options[0];
    const answer = findAnswerForOption(option)(answers);

    const value = answer ? prop("value")(answer) : "";

    let displayValue = { feet: undefined, inches: undefined, text: '' };
    if (value) {
      displayValue = this.convertToDisplayUnit(value, unitOfMeasure);
    }


    this.setState({
      selectedUnitOfMeasure: unitOfMeasure,
      displayValue
    })

    onAnswer(
      questionId,
      questionTypes.weight,
      option.answerId,
      value,
      {
        displayValue: displayValue,
        unitOfMeasure: unitOfMeasure
      }
    )
  }

  convertFromFt(displayValue) {
    return (parseInt(displayValue.inches) || 0) * 2.54 + (parseInt(displayValue.feet) || 0) * 30.48
  }

  convertFromDisplayUnit(value, selectedUnitOfMeasure) {
    const howToConvertToDefault = selectedUnitOfMeasure.conversionToDefault;
    let newValue = value
    if (howToConvertToDefault && howToConvertToDefault.operation === 'division') {
      switch (howToConvertToDefault.operation) {
      case 'division':{
        newValue = newValue / howToConvertToDefault.by
        break;
      }
      case 'multiplication':{
        newValue = newValue * howToConvertToDefault.by
        break;
      }
      }
    }
    return newValue;
  }

  convertCentimetresToFeet(centimetres, selectedUnitOfMeasure) {
    const cmInFoot = 30.48
    const cmInInch = 2.54;
    let displayValue = {
      feet: Math.floor(centimetres / cmInFoot)
    }
    displayValue.inches = Math.floor((centimetres - displayValue.feet * cmInFoot) / cmInInch)
    displayValue.text = `${displayValue.feet}' ${displayValue.inches}''`
    return displayValue
  }

  convertToDisplayUnit(value, selectedUnitOfMeasure) {
    if (value === '') {
      return value;
    }
    if (selectedUnitOfMeasure.unitOfMeasure === ftUnitOfMeasure) {
      return this.convertCentimetresToFeet(value, selectedUnitOfMeasure)
    }
    const howToConvertToDisplayUnit = selectedUnitOfMeasure.conversionFromDefault;
    let newValue = value
    if (howToConvertToDisplayUnit
      && howToConvertToDisplayUnit.operation === 'division') {
      const { by, operation } = howToConvertToDisplayUnit
      switch (operation) {
      case 'division':{
        newValue = newValue / by
        break;
      }
      case 'multiplication':{
        newValue = newValue * by
        break;
      }
      }
      newValue = newValue + '';
      if (newValue.indexOf('.') > -1) {
        newValue = newValue.split('.')[0];
      }
    }
    return newValue;
  }

  formatNumber(text) {
    if (!text)
      return ''
    if (text.length === 0)
      return ''
    return Number.parseFloat(Number.parseFloat(text).toFixed(2));
  }

  renderWithUnitsOfMeasure(option, value, unitsOfMeasure) {
    const {
      questionId,
      text,
      help,
      onAnswer,
      readOnly,
      validationState
    } = this.props

    return (
      <FormGroup
        controlId={questionId}
        validationState={validationState}
      >
        <ControlLabel>
          {text}
        </ControlLabel>
        <InputGroup className='col-md-3 input-form-group'>
          <FormControl
            type="weight"
            onChange={(event) => {
              this.setState({
                displayValue: { text: event.target.value }
              })
              const baseValue = this.convertFromDisplayUnit(event.target.value, this.state.selectedUnitOfMeasure);
              onAnswer(
                questionId,
                questionTypes.weight,
                option.answerId,
                baseValue + '',
                {
                  displayValue: { text: event.target.value },
                  unitOfMeasure: this.state.selectedUnitOfMeasure
                }
              )
            }}
            disabled={readOnly}
            placeholder={option.text}
            value={this.formatNumber(this.state.displayValue.text)}
          />
          { readOnly ? <InputGroup.Addon>{this.state.selectedUnitOfMeasure.unitOfMeasure}</InputGroup.Addon> :
            <DropdownButton id="heightDropDown" componentClass={InputGroup.Button} title={this.state.selectedUnitOfMeasure.unitOfMeasure}>
              { unitsOfMeasure.map(
                (unitOfMeasure) =>
                  <MenuItem
                    id={unitOfMeasure.unitOfMeasure}
                    onSelect={() => this.unitOfMeasureSelected(unitOfMeasure)}
                    key={unitOfMeasure.unitOfMeasure}>
                    {unitOfMeasure.unitOfMeasure}
                  </MenuItem>)
              }
            </DropdownButton>
          }
        </InputGroup>
        {help && <HelpBlock>{help}</HelpBlock>}
      </FormGroup>
    );
  }

  renderWithoutUnitsOfMeasure(option, value) {
    const {
      questionId,
      text,
      help,
      onAnswer,
      readOnly,
      showValidation,
      validationState
    } = this.props

    return (
      <FormGroup controlId={questionId} validationState={validationState}>
        <ControlLabel>
          {text}

        </ControlLabel>
        <FormControl
          type="weight"
          disabled={readOnly}
          placeholder={option.text}
          value={value}
          onChange={event =>
            onAnswer(
              questionId,
              questionTypes.weight,
              option.answerId,
              event.target.value
            )}
        />
        {help &&
        showValidation && (
            <HelpBlock>

              <Alert bsStyle="danger">{help}</Alert>
            </HelpBlock>
          )}
      </FormGroup>
    );
  }

  renderForFeet(option, value, unitsOfMeasure) {
    const {
      questionId,
      text,
      help,
      onAnswer,
      readOnly,
      validationState
    } = this.props

    return <FormGroup
      controlId={questionId}
      validationState={validationState}
    >
      <ControlLabel>
        {text}

      </ControlLabel>
      <InputGroup>
        <FormControl
          style={{ width: '50%' }}
          type="weight"
          onChange={(event) => {
            this.setState({
              displayValue: { feet: event.target.value, in: this.state.displayValue.inches }
            })
            const baseValue = this.convertFromFt({ feet: event.target.value, inches: this.state.displayValue.inches })
            onAnswer(
              questionId,
              questionTypes.height,
              option.answerId,
              baseValue + '',
              {
                displayValue: {
                  feet: event.target.value,
                  inches: this.state.displayValue.inches,
                  text: `${event.target.value} ft. ${this.state.displayValue.inches} in.`,
                },
                unitOfMeasure: this.state.selectedUnitOfMeasure
              }
            )
          }}
          disabled={readOnly}
          placeholder='feet'
          value={this.state.displayValue.feet}
        />
        <FormControl
          style={{ width: '50%' }}
          type="weight"
          onChange={(event) => {
            this.setState({
              displayValue: { feet: this.state.displayValue.feet, in: event.target.value }
            })
            const baseValue = this.convertFromFt({ feet: this.state.displayValue.feet, inches: event.target.value })
            onAnswer(
              questionId,
              questionTypes.weight,
              option.answerId,
              baseValue + '',
              {
                displayValue: {
                  feet: this.state.displayValue.feet,
                  inches: event.target.value,
                  text: `${this.state.displayValue.feet} ft. ${event.target.value} in.`,
                },
                unitOfMeasure: this.state.selectedUnitOfMeasure
              }
            )
          }}
          disabled={readOnly}
          placeholder='inches'
          value={this.state.displayValue.inches}
        />
        { readOnly ? <InputGroup.Addon>{this.state.selectedUnitOfMeasure.unitOfMeasure}</InputGroup.Addon> :
          <DropdownButton componentClass={InputGroup.Button} title={this.state.selectedUnitOfMeasure.unitOfMeasure}>
            { unitsOfMeasure.map(
              (unitOfMeasure) =>
                <MenuItem
                  id={unitOfMeasure.unitOfMeasure}
                  onSelect={() => this.unitOfMeasureSelected(unitOfMeasure)}
                  key={unitOfMeasure.unitOfMeasure}>
                  {unitOfMeasure.unitOfMeasure}
                </MenuItem>)
            }
          </DropdownButton>
        }
      </InputGroup>
      {help && <HelpBlock>{help}</HelpBlock>}
    </FormGroup>
  }

  render() {
    const { units, options, answers } = this.props

    const option = options[0];
    const answer = findAnswerForOption(option)(answers);

    const value = answer ? prop("value")(answer) : "";

    if (units) {
      if (this.state.selectedUnitOfMeasure.unitOfMeasure === ftUnitOfMeasure) {
        return this.renderForFeet(option, value, getUnitsOfMeasure(option));
      }
      return this.renderWithUnitsOfMeasure(option, value, getUnitsOfMeasure(option));
    }

    return this.renderWithoutUnitsOfMeasure(option, value);
  }
}

HeightQuestion.propTypes = {
  questionId: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  help: PropTypes.string,
  options: PropTypes.array.isRequired,
  answers: PropTypes.array,
  units: PropTypes.array,
  onAnswer: PropTypes.func,
  onUnitsChange: PropTypes.func,
  readOnly: PropTypes.bool,
  showValidation: PropTypes.bool,
  validationState: PropTypes.string,
  value: PropTypes.number
}

export default HeightQuestion;



// WEBPACK FOOTER //
// ./src/components/questions/HeightQuestion/HeightQuestion.js